import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    CollisionDetection,
    closestCenter,
    DndContext,
    DragOverEvent,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    pointerWithin,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Box, Paper, Typography, Fab, Stack } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useApiClient } from '../../../../ApiClientProvider';
import { ITask } from '../../../../interfaces/ITasks';
import AddTaskModal from '../../../Dialogs/AddTaskDialog';
import TaskDetailsModal from '../../../Dialogs/TaskDetailDialog';
import { EventService } from '../../../../services/eventService';
import TaskColumn from './TaskColumn';
import TaskItemCard from './TaskItemCard';
import { EventTaskStatus } from '../../../../enums/EventTaskStatus';
import { useNotifications } from '@toolpad/core';
import { useFeature } from '../../../../hooks/useFeature';
import { FeatureKeys } from '../../../../constants/featureKeys';
import FeatureLockedContent from '../../../Features/FeatureLockedContent';
import RefreshButton from '../../../RefreshButton';

interface TaskBoardProps {
    eventId?: string;
    eventIds?: string[];
    eventOptions?: Array<{ id: string; title: string }>;
    eventTitle?: string;
    onUpdateEvent?: (eventId: string) => void;
}

const TaskBoard: React.FC<TaskBoardProps> = ({ eventId, eventIds, eventOptions, eventTitle, onUpdateEvent }) => {
    const [tasks, setTasks] = useState<ITask[]>([]);
    const [loading, setLoading] = useState(false);
    const [metricsTimestamp, setMetricsTimestamp] = useState(0);
    const [addTaskModalOpen, setAddTaskModalOpen] = useState(false);
    const [selectedColumnStatus, setSelectedColumnStatus] = useState<EventTaskStatus>(EventTaskStatus.New);
    const [selectedTask, setSelectedTask] = useState<ITask | null>(null);
    const [activeTask, setActiveTask] = useState<ITask | null>(null);
    const [activeColumnId, setActiveColumnId] = useState<string | null>(null);
    const [addTaskEventId, setAddTaskEventId] = useState<string>('');
    const dragStartTasksRef = useRef<ITask[] | null>(null);
    const previewUpdateRafRef = useRef<number | null>(null);
    const lastPreviewSignatureRef = useRef<string>('');

    const apiClient = useApiClient('applicationClient');
    const eventService = useMemo(() => new EventService(apiClient), [apiClient]);
    const notifications = useNotifications();

    // Check TaskManagementEnabled feature
    const tasksFeature = useFeature(FeatureKeys.TaskManagementEnabled);

    const isMultiEvent = !!eventIds && eventIds.length > 1;
    const effectiveEventId = eventId ?? '';

    const fetchEventTasks = useCallback(async () => {
        if (!tasksFeature.enabled) return;

        if (!isMultiEvent && !effectiveEventId) return;
        setLoading(true);
        try {
            const data = isMultiEvent && eventIds
                ? await eventService.getTasksForMultipleEvents(eventIds)
                : await eventService.getEventTasks(effectiveEventId);
            setTasks(data);
            setMetricsTimestamp(Date.now());
        } catch {
            notifications.show('We could not refresh your tasks. Try again.', { autoHideDuration: 3000, severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [effectiveEventId, eventIds, isMultiEvent, eventService, notifications, tasksFeature.enabled]);

    useEffect(() => {
        fetchEventTasks();
    }, [fetchEventTasks]);

    useEffect(() => {
        return () => {
            if (previewUpdateRafRef.current !== null) {
                cancelAnimationFrame(previewUpdateRafRef.current);
            }
        };
    }, []);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    const collisionDetectionStrategy: CollisionDetection = (args) => {
        const pointerIntersections = pointerWithin(args);
        if (pointerIntersections.length > 0) {
            return pointerIntersections;
        }

        return closestCenter(args);
    };

    const columns = [
        { id: EventTaskStatus.New, title: 'New', color: '#2196f3' },
        { id: EventTaskStatus.Active, title: 'Active', color: '#ff9800' },
        { id: EventTaskStatus.Closed, title: 'Closed', color: '#4caf50' },
    ];

    const taskMetrics = useMemo(() => {
        return {
            total: tasks.length,
            new: tasks.filter(task => task.status === EventTaskStatus.New).length,
            active: tasks.filter(task => task.status === EventTaskStatus.Active).length,
            closed: tasks.filter(task => task.status === EventTaskStatus.Closed).length,
            overdue: tasks.filter(task =>
                task.status !== EventTaskStatus.Closed
                && task.dueDate != null
                && new Date(task.dueDate).getTime() < metricsTimestamp
            ).length,
        };
    }, [metricsTimestamp, tasks]);

    const getStatusFromOverId = (
        overIdStr: string,
        currentTasks: ITask[],
        fallbackStatus: EventTaskStatus
    ): EventTaskStatus => {
        const overTask = currentTasks.find(task => task.id === overIdStr);
        if (overTask) {
            return overTask.status;
        }

        const overIdNumber = Number(overIdStr);
        if (!isNaN(overIdNumber) && Object.values(EventTaskStatus).includes(overIdNumber)) {
            return overIdNumber as EventTaskStatus;
        }

        return fallbackStatus;
    };

    const getStatusFromSortableContainerId = (
        containerId: unknown,
        fallbackStatus: EventTaskStatus
    ): EventTaskStatus => {
        const containerIdNumber = Number(containerId);
        if (!isNaN(containerIdNumber) && Object.values(EventTaskStatus).includes(containerIdNumber)) {
            return containerIdNumber as EventTaskStatus;
        }

        return fallbackStatus;
    };

    const getDragCenterY = (translatedRect: { top: number; height: number } | null): number | null => {
        if (!translatedRect) {
            return null;
        }

        return translatedRect.top + translatedRect.height / 2;
    };

    const getStatusFromDragPosition = (
        translatedRect: { top: number; left: number; width: number; height: number } | null
    ): EventTaskStatus | null => {
        if (!translatedRect) {
            return null;
        }

        const centerX = translatedRect.left + translatedRect.width / 2;
        const centerY = translatedRect.top + translatedRect.height / 2;
        const laneElements = Array.from(document.querySelectorAll<HTMLElement>('[data-task-column-id]'));

        for (const laneElement of laneElements) {
            const rect = laneElement.getBoundingClientRect();
            const isInsideLane = centerX >= rect.left && centerX <= rect.right && centerY >= rect.top && centerY <= rect.bottom;

            if (isInsideLane) {
                const laneId = Number(laneElement.dataset.taskColumnId);
                if (!isNaN(laneId) && Object.values(EventTaskStatus).includes(laneId)) {
                    return laneId as EventTaskStatus;
                }
            }
        }

        return null;
    };

    const buildTasksWithMovedTask = (
        currentTasks: ITask[],
        movingTask: ITask,
        targetStatus: EventTaskStatus,
        insertIndex: number
    ): ITask[] => {
        const filteredTasks = currentTasks.filter(task => task.id !== movingTask.id);
        const nextTasks: ITask[] = [];
        let inserted = false;
        let statusCount = 0;

        for (const task of filteredTasks) {
            if (task.status === targetStatus) {
                if (!inserted && statusCount === insertIndex) {
                    nextTasks.push({ ...movingTask, status: targetStatus });
                    inserted = true;
                }
                statusCount += 1;
            }

            nextTasks.push(task);
        }

        if (!inserted) {
            nextTasks.push({ ...movingTask, status: targetStatus });
        }

        return nextTasks;
    };

    const areTaskOrdersEqual = (left: ITask[], right: ITask[]): boolean => {
        if (left.length !== right.length) {
            return false;
        }

        for (let i = 0; i < left.length; i += 1) {
            if (left[i].id !== right[i].id || left[i].status !== right[i].status) {
                return false;
            }
        }

        return true;
    };

    const getTaskSignature = (items: ITask[]): string => {
        return items.map(task => `${task.id}:${task.status}`).join('|');
    };

    const clearPreviewUpdateRaf = () => {
        if (previewUpdateRafRef.current !== null) {
            cancelAnimationFrame(previewUpdateRafRef.current);
            previewUpdateRafRef.current = null;
        }
    };

    const schedulePreviewUpdate = (nextTasks: ITask[]) => {
        const signature = getTaskSignature(nextTasks);
        if (lastPreviewSignatureRef.current === signature) {
            return;
        }

        clearPreviewUpdateRaf();
        previewUpdateRafRef.current = requestAnimationFrame(() => {
            setTasks(nextTasks);
            lastPreviewSignatureRef.current = signature;
            previewUpdateRafRef.current = null;
        });
    };

    const handleDragStart = (event: DragStartEvent) => {
        const task = event.active.data.current?.task;
        setActiveTask(task);
        dragStartTasksRef.current = tasks;
        lastPreviewSignatureRef.current = getTaskSignature(tasks);
    };

    const handleDragOver = (event: DragOverEvent) => {
        const activeId = String(event.active.id);
        const overId = event.over?.id;
        const baseTasks = dragStartTasksRef.current ?? tasks;

        if (!overId) {
            setActiveColumnId(prev => (prev === null ? prev : null));
            return;
        }

        const overIdStr = String(overId);
        const overSortableContainerId = event.over?.data?.current?.sortable?.containerId;
        const overTask = overIdStr === activeId ? undefined : baseTasks.find(task => task.id === overIdStr);
        const statusFromPosition = getStatusFromDragPosition(event.active.rect.current.translated);

        const resolvedStatus = overSortableContainerId !== undefined
            ? getStatusFromSortableContainerId(overSortableContainerId, activeTask?.status ?? EventTaskStatus.New)
            : undefined;

        if (statusFromPosition !== null) {
            const laneId = String(statusFromPosition);
            setActiveColumnId(prev => (prev === laneId ? prev : laneId));
        } else if (resolvedStatus !== undefined) {
            const laneId = String(resolvedStatus);
            setActiveColumnId(prev => (prev === laneId ? prev : laneId));
        } else if (overTask) {
            const laneId = String(overTask.status);
            setActiveColumnId(prev => (prev === laneId ? prev : laneId));
        } else {
            const overColumnId = Number(overIdStr);
            if (!isNaN(overColumnId) && Object.values(EventTaskStatus).includes(overColumnId)) {
                const laneId = String(overColumnId);
                setActiveColumnId(prev => (prev === laneId ? prev : laneId));
            } else {
                setActiveColumnId(prev => (prev === null ? prev : null));
            }
        }

        const currentActiveTask = baseTasks.find(task => task.id === activeId) ?? activeTask;
        if (!currentActiveTask) {
            return;
        }

        const newStatus = statusFromPosition
            ?? resolvedStatus
            ?? getStatusFromOverId(overIdStr, baseTasks, currentActiveTask.status);
        const filteredTasks = baseTasks.filter(task => task.id !== activeId);
        const targetColumnTasks = filteredTasks.filter(task => task.status === newStatus);
        const dragCenterY = getDragCenterY(event.active.rect.current.translated);

        let insertIndex = targetColumnTasks.length;
        if (overTask) {
            const overIndexInColumn = targetColumnTasks.findIndex(task => task.id === overIdStr);
            if (overIndexInColumn !== -1) {
                if (dragCenterY === null) {
                    insertIndex = overIndexInColumn;
                } else {
                    const overElement = document.querySelector(`[data-task-id="${overIdStr}"]`) as HTMLElement | null;
                    if (overElement) {
                        const overRect = overElement.getBoundingClientRect();
                        const midpointY = overRect.top + overRect.height / 2;
                        insertIndex = dragCenterY > midpointY ? overIndexInColumn + 1 : overIndexInColumn;
                    } else {
                        insertIndex = overIndexInColumn;
                    }
                }
            }
        } else {
            insertIndex = getInsertIndexFromPointer(newStatus, targetColumnTasks, dragCenterY);
        }

        const previewTasks = buildTasksWithMovedTask(baseTasks, currentActiveTask, newStatus, insertIndex);
        if (!areTaskOrdersEqual(tasks, previewTasks)) {
            schedulePreviewUpdate(previewTasks);
        }
    };

    const getDropY = (event: DragEndEvent): number | null => {
        const translatedRect = event.active.rect.current.translated;
        if (!translatedRect) {
            return null;
        }

        return translatedRect.top + translatedRect.height / 2;
    };

    const getInsertIndexFromPointer = (
        columnStatus: EventTaskStatus,
        columnTasks: ITask[],
        pointerY: number | null
    ): number => {
        if (pointerY === null || columnTasks.length === 0) {
            return columnTasks.length;
        }

        const columnElement = document.querySelector(`[data-task-column-id="${columnStatus}"]`) as HTMLElement | null;
        if (!columnElement) {
            return columnTasks.length;
        }

        const taskElements = Array.from(
            columnElement.querySelectorAll<HTMLElement>('[data-task-id]')
        );

        for (let index = 0; index < taskElements.length; index += 1) {
            const rect = taskElements[index].getBoundingClientRect();
            const midpointY = rect.top + rect.height / 2;

            if (pointerY < midpointY) {
                return index;
            }
        }

        return columnTasks.length;
    };

    const handleDragEnd = async (dragEvent: DragEndEvent) => {
        const { active, over } = dragEvent;
        clearPreviewUpdateRaf();
        setActiveTask(null);
        setActiveColumnId(null);

        if (!over) {
            if (dragStartTasksRef.current) {
                setTasks(dragStartTasksRef.current);
            }
            dragStartTasksRef.current = null;
            lastPreviewSignatureRef.current = '';
            return;
        }

        const taskId = active.id as string;
        const overId = over.id as string;
        const baseTasks = dragStartTasksRef.current ?? tasks;

        const activeTask = baseTasks.find(t => t.id === taskId);
        if (!activeTask) return;
        const statusFromPosition = getStatusFromDragPosition(active.rect.current.translated);
        const overSortableContainerId = over.data?.current?.sortable?.containerId;
        const overTask = overId === taskId ? undefined : baseTasks.find(t => t.id === overId);

        // Determine new status (column)
        let newStatus: EventTaskStatus;
        if (statusFromPosition !== null) {
            newStatus = statusFromPosition;
        } else if (overSortableContainerId !== undefined) {
            newStatus = getStatusFromSortableContainerId(overSortableContainerId, activeTask.status);
        } else if (overTask) {
            newStatus = overTask.status;
        } else {
            const overIdNumber = Number(overId);
            if (!isNaN(overIdNumber) && Object.values(EventTaskStatus).includes(overIdNumber)) {
                newStatus = overIdNumber as EventTaskStatus;
            } else {
                newStatus = activeTask.status;
            }
        }

        // Find all tasks in the new column (after move)
        const filteredTasks = baseTasks.filter(t => t.id !== taskId);
        const targetColumnTasks = filteredTasks.filter(t => t.status === newStatus);

        // Find the index to insert at
        let insertIndex = targetColumnTasks.length; // default to end
        if (overTask) {
            // Insert before the overTask in the new column
            const overIndexInColumn = targetColumnTasks.findIndex(t => t.id === overId);
            if (overIndexInColumn !== -1) {
                insertIndex = overIndexInColumn;
            }
        } else {
            insertIndex = getInsertIndexFromPointer(
                newStatus,
                targetColumnTasks,
                getDropY(dragEvent)
            );
        }

        const updatedTasks = buildTasksWithMovedTask(baseTasks, activeTask, newStatus, insertIndex);

        setTasks(updatedTasks);
        lastPreviewSignatureRef.current = getTaskSignature(updatedTasks);

        try {
            const taskEventId = isMultiEvent ? (activeTask.eventId ?? effectiveEventId) : effectiveEventId;
            await eventService.UpdateTask(taskEventId, activeTask?.id ?? '', { ...activeTask, status: newStatus });
            if (onUpdateEvent) onUpdateEvent(taskEventId);
        } catch {
            // Rollback on error
            if (dragStartTasksRef.current) {
                setTasks(dragStartTasksRef.current);
            }
            notifications.show('Failed to update task status. Please try again.', {
                autoHideDuration: 3000,
                severity: 'error',
            });
        } finally {
            dragStartTasksRef.current = null;
            lastPreviewSignatureRef.current = '';
        }
    };

    const handleAddTask = (newTask: ITask) => {
        const taskEventId = isMultiEvent ? (newTask.eventId ?? effectiveEventId) : effectiveEventId;
        const task: ITask = {
            ...newTask,
            status: selectedColumnStatus,
            id: newTask.id || '',
            createdAt: new Date(),
        };

        const updatedTasks = [...tasks, task];
        setTasks(updatedTasks);
        if (onUpdateEvent) onUpdateEvent(taskEventId);
    };

    const handleUpdateTask = async (updatedTask: ITask) => {
        const taskEventId = isMultiEvent ? (updatedTask.eventId ?? effectiveEventId) : effectiveEventId;
        const updatedTasks = tasks.map(task =>
            task.id === updatedTask.id ? updatedTask : task
        );
        setTasks(updatedTasks);
        if (onUpdateEvent) onUpdateEvent(taskEventId);
    };

    const handleDeleteTask = async (taskId: string) => {
        const taskToDelete = tasks.find(t => t.id === taskId);
        const taskEventId = isMultiEvent ? (taskToDelete?.eventId ?? effectiveEventId) : effectiveEventId;
        try {
            await eventService.DeleteTask(taskEventId, taskId);
            if (selectedTask?.id === taskId) {
                setSelectedTask(null);
            }
            const updatedTasks = tasks.filter(task => task.id !== taskId);
            setTasks(updatedTasks);
            if (onUpdateEvent) onUpdateEvent(taskEventId);
        } catch {
            if (onUpdateEvent) onUpdateEvent(taskEventId);
            notifications.show('Failed to delete task. Please try again.', {
                autoHideDuration: 3000,
                severity: 'error'
            });
        }
    };

    const handleAddTaskFromColumn = (columnStatus: EventTaskStatus) => {
        setSelectedColumnStatus(columnStatus);
        if (isMultiEvent && eventIds && eventIds.length > 0) {
            setAddTaskEventId(eventIds[0]);
        }
        setAddTaskModalOpen(true);
    };

    if (!tasksFeature.enabled) {
        return (
            <FeatureLockedContent
                featureKey={FeatureKeys.TaskManagementEnabled}
                title="Task Management"
                description="Organise and track event tasks with a visual task board. Assign tasks to team members and monitor progress."
                suggestedTier="Basic"
            />
        );
    }

    return (
        <Box sx={{
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            height: "calc(100vh - 180px)",

        }}
        >
            {/* Header Row */}
            <Box sx={{ flexShrink: 0, p: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                    <Box>
                        {eventTitle && (
                            <Typography variant="h6" fontWeight="bold">
                                {eventTitle} - Task Board
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            Drag tasks between columns to update their status
                        </Typography>
                    </Box>
                    <Stack direction="row" gap={1} alignItems="center">
                    <RefreshButton ariaLabel="Refresh tasks" onRefresh={fetchEventTasks} loading={loading}
                        disabled={Boolean(activeTask || selectedTask || addTaskModalOpen)} />
                    <Fab
                        color="primary"
                        size="small"
                        onClick={() => {
                            if (isMultiEvent && eventIds && eventIds.length > 0) {
                                setAddTaskEventId(eventIds[0]);
                            }
                            setAddTaskModalOpen(true);
                        }}
                        sx={{ width: 40, height: 40 }}
                    >
                        <Add />
                    </Fab>
                    </Stack>
                </Box>
                <Paper
                    variant="outlined"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: { xs: 1.25, sm: 2 },
                        mx: 2,
                        mb: 2,
                        px: 1.5,
                        py: 1,
                        border: '0.5px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, pr: { sm: 1 } }}>
                        <Typography variant="h6" fontWeight={500}>
                            {taskMetrics.total}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            total tasks
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.25, sm: 2 } }}>
                        {[
                            { label: 'New', value: taskMetrics.new, colour: '#2196f3' },
                            { label: 'Active', value: taskMetrics.active, colour: '#ff9800' },
                            { label: 'Closed', value: taskMetrics.closed, colour: '#4caf50' },
                            { label: 'Overdue', value: taskMetrics.overdue, colour: 'error.main' },
                        ].map(metric => (
                            <Box key={metric.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: metric.colour,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography variant="body2" fontWeight={500}>
                                    {metric.value}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {metric.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Paper>
            </Box>

            {/* DnD Context with Columns */}
            <DndContext
                sensors={sensors}
                collisionDetection={collisionDetectionStrategy}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDragCancel={() => {
                    clearPreviewUpdateRaf();
                    if (dragStartTasksRef.current) {
                        setTasks(dragStartTasksRef.current);
                    }
                    dragStartTasksRef.current = null;
                    lastPreviewSignatureRef.current = '';
                    setActiveTask(null);
                    setActiveColumnId(null);
                }}
            >
                <Box sx={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    display: 'flex',
                    gap: 2,
                    flexDirection: 'row',
                    overflow: 'hidden',
                }}>
                    {columns.map((column) => {
                        const columnTasks = tasks.filter((task: ITask) => task.status === column.id);
                        return (
                            <Box key={column.id} sx={{
                                flex: '1 1 0',
                                minWidth: 0,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                            }}>
                                <Paper sx={{
                                    p: 1.5,
                                    flex: 1,
                                    minHeight: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                    border: '1.5px dotted',
                                    borderColor: 'divider',
                                    borderRadius: 0,
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexShrink: 0 }}>
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                backgroundColor: column.color,
                                                mr: 0.75,
                                            }}
                                        />
                                        <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '0.95rem' }}>
                                            {column.title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                                            ({columnTasks.length})
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            flex: 1,
                                            overflowY: "auto",
                                            minHeight: 0,

                                            scrollbarWidth: "thin",
                                            scrollbarColor: "rgba(128,128,128,0.35) transparent",

                                            "&::-webkit-scrollbar": {
                                                width: "4px"
                                            },
                                            "&::-webkit-scrollbar-thumb": {
                                                background: "rgba(128,128,128,0.35)",
                                                borderRadius: "10px"
                                            },
                                            "&::-webkit-scrollbar-thumb:hover": {
                                                background: "rgba(128,128,128,0.55)"
                                            }
                                        }}
                                    >
                                        <SortableContext
                                            items={columnTasks.map(task => task.id ?? Date.now.toString())}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            <TaskColumn
                                                id={column.id.toString()}
                                                tasks={columnTasks}
                                                onTaskClick={setSelectedTask}
                                                onAddTask={() => handleAddTaskFromColumn(column.id as EventTaskStatus)}
                                                isActiveDropTarget={activeColumnId === column.id.toString()}
                                            />
                                        </SortableContext>
                                    </Box>
                                </Paper>
                            </Box>
                        );
                    })}
                </Box>

                <DragOverlay>
                    {activeTask ? (
                        <Box sx={{ transform: 'rotate(5deg)' }}>
                            <TaskItemCard
                                task={activeTask}
                                onClick={() => { }}
                                isDragging={true}
                            />
                        </Box>
                    ) : null}
                </DragOverlay>
            </DndContext>

            {/* Modals */}
            <AddTaskModal
                open={addTaskModalOpen}
                onClose={() => setAddTaskModalOpen(false)}
                onAdd={handleAddTask}
                eventId={isMultiEvent ? addTaskEventId : effectiveEventId}
                events={isMultiEvent ? eventOptions : undefined}
            />

            {selectedTask && (
                <TaskDetailsModal
                    eventId={isMultiEvent ? (selectedTask.eventId ?? effectiveEventId) : effectiveEventId}
                    task={selectedTask}
                    onClose={() => setSelectedTask(null)}
                    onUpdate={handleUpdateTask}
                    onDelete={handleDeleteTask}
                />
            )}
        </Box>
    );
};

export default TaskBoard;